import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/NewInvestment.css";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import heroImg from "../images/trade-girl.webp";
import MarketMarquee from "../components/stocks/MarketMarque";
import LocationMap from "../components/LocationMap";

const alphaData = [
  { id: "defi-alpha", name: "DeFi Alpha", symbol: "DEFI", price: 2.45, change: 12.5 },
  { id: "ai-tokens", name: "AI Tokens", symbol: "AI", price: 1.82, change: 8.3 },
  { id: "gamefi", name: "GameFi", symbol: "GAME", price: 0.52, change: -3.2 },
  { id: "layer2", name: "Layer 2", symbol: "L2", price: 1.15, change: 5.7 },
  { id: "web3", name: "Web3", symbol: "W3", price: 0.85, change: 4.2 },
];

const coinLogos = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.png",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.png",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.png",
  XRP: "https://cryptologos.cc/logos/xrp-xrp-logo.png",
  ADA: "https://cryptologos.cc/logos/cardano-ada-logo.png",
  DOGE: "https://cryptologos.cc/logos/dogecoin-doge-logo.png",
  DOT: "https://cryptologos.cc/logos/polkadot-new-dot-logo.png",
  AAPL: "https://logo.clearbit.com/apple.com",
  MSFT: "https://logo.clearbit.com/microsoft.com",
  NVDA: "https://logo.clearbit.com/nvidia.com",
  GOOGL: "https://logo.clearbit.com/google.com",
  TSLA: "https://logo.clearbit.com/tesla.com",
  AMZN: "https://logo.clearbit.com/amazon.com",
};

const NewInvestment = ({ user }) => {
  const navigate = useNavigate();
  const [recommendedPlan, setRecommendedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketLoading, setMarketLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Alphas");
  const [cryptoData, setCryptoData] = useState([]);

  // Fetch market data from CoinGecko
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const cryptoRes = await fetch(
          'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=bitcoin,ethereum,solana,binancecoin,ripple,cardano,dogecoin,polkadot&order=market_cap_desc&sparkline=false'
        );
        if (cryptoRes.ok) {
          const cryptoJson = await cryptoRes.json();
          const formattedCrypto = cryptoJson.map(coin => ({
            id: coin.id,
            name: coin.name,
            symbol: coin.symbol.toUpperCase(),
            price: coin.current_price,
            change: coin.price_change_percentage_24h || 0
          }));
          setCryptoData(formattedCrypto);
        }
      } catch (err) {
        console.log('Failed to fetch crypto data');
      }

      setMarketLoading(false);
    };

    fetchMarketData();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const docRef = doc(db, "users", "plans");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const plansData = docSnap.data();
          const plansArray = Object.entries(plansData).map(([key, plan]) => ({
            key,
            ...plan,
          }));

          setPlans(plansArray);
          if (plansArray.length > 0) {
            setRecommendedPlan(plansArray[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching plans:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePlanClick = () => {
    navigate(user ? "/fxt/trade" : "/login");
  };

  const getMarketData = () => {
    let data;
    switch (activeTab) {
      case "Crypto":
        data = cryptoData;
        break;
      case "Stocks":
        data = [];
        break;
      case "Alphas":
        data = alphaData;
        break;
      case "Favourites":
        data = cryptoData.slice(0, 5);
        break;
      default:
        data = cryptoData;
    }
    return data.slice(0, 5);
  };


  const formatPrice = (price) => {
    if (price >= 1000) return price.toLocaleString();
    if (price >= 1) return price.toFixed(2);
    return price.toFixed(4);
  };

  return (
    <div className="new-invest-page">
      <section className="section-hero">
        <div className="hero-top">
          <div className="hero-card image-card">
            <img src={heroImg} alt="Investments" className="hero-img" />
            <div className="image-content">
              <h2 className="hero-titles">Investments for Everyone!</h2>
              <p className="hero-text">
                Secure your financial future with our premium investment plans.
              </p>
              <button className="hero-btn" onClick={() => navigate("/login")}>
                Learn More
              </button>
            </div>
          </div>

          <div className="hero-card plan-card">
            {loading ? (
              <p className="loading">Loading...</p>
            ) : recommendedPlan ? (
              <>
                <div className="plan-header">
                  <span className="plan-badge">Recommended</span>
                </div>
                <div className="plan-name-price">
                  <h3 className="plan-name">{recommendedPlan.name}</h3>
                  <span className="plan-price">{recommendedPlan.price}</span>
                </div>
                <div className="plan-stats">
                  <div className="stat">
                    <span className="stat-label">Duration</span>
                    <span className="stat-value">
                      {recommendedPlan.durationDisplay}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Profit</span>
                    <span className="stat-value highlight">
                      +{recommendedPlan.profit}%
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">TPS</span>
                    <span className="stat-value">{recommendedPlan.maxTps}</span>
                  </div>
                </div>
                <button className="plan-btn" onClick={handlePlanClick}>
                  Invest Now
                </button>
              </>
            ) : (
              <p className="loading">No plans available</p>
            )}
          </div>
        </div>
      </section>
        <MarketMarquee />

      {/* Market Analysis Section */}
      <section className="market-section">
        <h2 className="section-title">Market Analysis</h2>

        <div className="market-tabs">
          {["Favourites", "Crypto", "Stocks", "Alphas"].map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="market-list">
          {marketLoading ? (
            <div className="market-loading">Loading market data...</div>
          ) : getMarketData().length === 0 ? (
            <div className="market-loading">...</div>
          ) : (
            getMarketData().map((item, index) => (
              <a
                key={`${activeTab}-${item.id}`}
                className="market-row"
                href={`https://www.coingecko.com/en/coins/${item.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="row-num">{index + 1}</span>
                <div className="row-coin">
                  <img
                    src={
                      coinLogos[item.symbol] ||
                      `https://ui-avatars.com/api/?name=${item.symbol}&background=D4292A&color=fff`
                    }
                    alt={item.name}
                    className="coin-logo"
                    onError={(e) =>
                      (e.target.src = `https://ui-avatars.com/api/?name=${item.symbol}&background=D4292A&color=fff`)
                    }
                  />
                  <div className="coin-info">
                    <span className="coin-name">{item.name}</span>
                    <span className="coin-symbol">{item.symbol}</span>
                  </div>
                </div>
                <span className="row-price">${formatPrice(item.price)}</span>
                <span
                  className={`row-change ${item.change >= 0 ? "up" : "down"}`}
                >
                  {item.change >= 0 ? "+" : ""}
                  {item.change.toFixed(2)}%
                </span>
              </a>
            ))
          )}
        </div>
      </section>

      {/* Investment Plans Section */}
      <section className="plans-section">
        <h2 className="section-title">Investment Plans</h2>
        <p className="section-subtitle">
          Choose a plan that suits your investment goals.
        </p>
        <div className="plans-grid">
          {loading ? (
            <p className="loading">Loading plans...</p>
          ) : plans.length > 0 ? (
            plans.map((plan) => {
              let specsArr = [];
              if (Array.isArray(plan.specs)) {
                specsArr = plan.specs;
              } else if (plan.specs && typeof plan.specs === 'object') {
                specsArr = Object.values(plan.specs).filter(v => typeof v === 'string');
              }

              return (
                <div
                  key={plan.key}
                  className="plan-card-click"
                  onClick={() => navigate(`/investments/${plan.key}`)}
                >
                  <h3 className="plan-card-name">{plan.name}</h3>
                  <span className="plan-card-price">{plan.price}</span>
                  
                  {plan.hardware && (
                    <div className="card-hardware">{plan.hardware}</div>
                  )}

                  {specsArr.length > 0 && (
                    <div className="card-perks">
                      <span className="perks-label">Perks:</span>
                      <ul>
                        {specsArr.map((spec, idx) => (
                          <li key={idx}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                <div className="plan-card-stats">
                  <span>{plan.durationDisplay}</span>
                  <span className="profit">+{plan.profit}%</span>
                </div>
              </div>
              );
            })
          ) : (
            <p className="loading">No plans available</p>
          )}
        </div>
      </section>


       <div className="map-box">
        <LocationMap />
      </div>
    </div>
  );
};

export default NewInvestment;
